import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, Copy, Plus, Trash2, X } from 'lucide-react';
import clsx from 'clsx';
import { day, del, get, post } from '../lib/api';
import { Badge, Button, Card, ErrorNote, Field, Input, Loading, PageHeader, Select } from '../ui';

/**
 * The connection, the keys and the webhooks.
 *
 * The Google section is mostly a checklist on purpose. The thing that stops
 * people is not the code, it is not knowing that the OAuth client and the
 * developer token are two different objects from two different consoles.
 */

type Status = {
  ready: {
    oauth_client: boolean; developer_token: boolean; token_key: boolean;
    login_customer_id: string | null; redirect_uri: string; api_version: string;
  };
  connection: null | {
    email: string | null; login_customer_id: string | null;
    connected_at: string; last_used_at: string | null; last_error: string | null;
  };
  note: string | null;
};

export default function Settings() {
  return (
    <>
      <Link to="/" className="mb-4 inline-flex items-center gap-1.5 text-sm text-t2 hover:text-t1">
        <ArrowLeft className="size-4" /> Accounts
      </Link>
      <PageHeader title="Settings" subtitle="The Google connection, keys for other systems, and where findings get sent." />
      <div className="space-y-6">
        <Google />
        <Keys />
        <Hooks />
      </div>
    </>
  );
}

function Google() {
  const qc = useQueryClient();
  const [mcc, setMcc] = useState('');
  const q = useQuery({ queryKey: ['google'], queryFn: () => get<Status>('/v1/google/status') });

  const connect = useMutation({
    mutationFn: () => post<{ url: string }>('/v1/google/start', { login_customer_id: mcc || undefined }),
    onSuccess: (r) => { window.location.href = r.url; },
  });
  const disconnect = useMutation({
    mutationFn: () => del('/v1/google'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['google'] }),
  });

  if (q.isLoading) return <Card><Loading /></Card>;
  if (q.error || !q.data) return <Card><ErrorNote error={q.error} /></Card>;
  const { ready, connection, note } = q.data;
  // The developer token is deliberately not part of this. Google's own API
  // Center now says it is not required, so refusing to let anybody press
  // Connect without one would block a setup that works.
  const allReady = ready.oauth_client && ready.token_key;

  const rows = [
    {
      ok: ready.oauth_client,
      label: 'OAuth client',
      how: <>A Web client in the Cloud console, with <code className="num">{ready.redirect_uri}</code> as an authorised
        redirect URI. Goes in <code className="num">GOOGLE_CLIENT_ID</code> and <code className="num">GOOGLE_CLIENT_SECRET</code>.</>,
    },
    {
      ok: ready.token_key,
      label: 'Encryption key',
      how: <>Seals the stored refresh token so a database dump is not a working credential.
        <code className="num"> ADS_TOKEN_KEY</code>, created by the deploy script. Never rotate it.</>,
    },
    {
      ok: true,
      optional: true,
      label: ready.developer_token ? 'Developer token — set' : 'Developer token — not set, and that is fine',
      how: <>Google&rsquo;s API Center now says it is no longer required, and that access levels are managed in the
        Cloud console instead. It is sent when present and left out when not. What actually limits you is the
        <b> access level</b> on the Google Ads API in the Cloud console: Explorer allows real accounts at 2,880
        operations a day, Basic at 15,000. One account sync here is six operations.</>,
    },
  ];

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold">Google Ads</h2>
          <p className="mt-0.5 max-w-prose text-sm text-t2">
            One connection for the whole agency. Your manager account is linked read-only to each client, so a single
            sign-in reads all of them — the client never sees an OAuth screen.
          </p>
        </div>
        {connection ? (
          <Button loading={disconnect.isPending} onClick={() => disconnect.mutate()}>Disconnect</Button>
        ) : (
          <Button variant="primary" disabled={!allReady} loading={connect.isPending} onClick={() => connect.mutate()}>
            Connect Google
          </Button>
        )}
      </div>

      {connection && (
        <div className="mt-4 rounded-btn border border-bd bg-bg3 px-3 py-2 text-sm">
          Connected{connection.email ? <> as <b>{connection.email}</b></> : ''}
          {connection.login_customer_id && <> · manager <span className="num">{connection.login_customer_id}</span></>}
          {' · '}since {day(connection.connected_at)}
          {connection.last_error && <div className="mt-1 text-err">Last error: {connection.last_error}</div>}
        </div>
      )}

      <ul className="mt-4 space-y-3">
        {rows.map((r) => (
          <li key={r.label} className="flex gap-3">
            <span className={clsx('mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full',
              !r.ok ? 'bg-err-bg text-err' : r.optional ? 'bg-bg3 text-t3' : 'bg-ok-bg text-ok')}>
              {r.ok ? <Check className="size-3" /> : <X className="size-3" />}
            </span>
            <div>
              <div className="text-sm font-medium">{r.label}</div>
              <p className="text-xs text-t2">{r.how}</p>
            </div>
          </li>
        ))}
      </ul>

      {note && (
        <p className="mt-4 rounded-btn border border-[color-mix(in_srgb,var(--warn)_35%,transparent)] bg-warn-bg px-3 py-2 text-sm text-warn">
          {note}
        </p>
      )}

      {!connection && allReady && (
        <div className="mt-4 max-w-xs">
          <Field label="Manager account ID"
            hint={ready.login_customer_id
              ? `Defaults to ${ready.login_customer_id} from the server. Override it here if you need to.`
              : 'The MCC every request goes through.'}>
            <Input value={mcc} onChange={(e) => setMcc(e.target.value)}
              placeholder={ready.login_customer_id ?? '123-456-7890'} />
          </Field>
        </div>
      )}
      <ErrorNote error={connect.error ?? disconnect.error} />
    </Card>
  );
}

type Key = { id: string; name: string; prefix: string; scopes: string[]; created_at: string; last_used_at: string | null; revoked_at: string | null };

function Keys() {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [made, setMade] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['keys'], queryFn: () => get<{ data: Key[] }>('/v1/api-keys') });
  const create = useMutation({
    mutationFn: () => post<Key & { key: string }>('/v1/api-keys', { name }),
    onSuccess: (r) => { setMade(r.key); setName(''); qc.invalidateQueries({ queryKey: ['keys'] }); },
  });

  return (
    <Card>
      <h2 className="text-sm font-bold">Keys for other systems</h2>
      <p className="mt-0.5 max-w-prose text-sm text-t2">
        Read-only. Something automated should be able to ask what an audit found; nothing automated should be able to
        add an account or spend Google quota.
      </p>

      {made && (
        <div className="mt-3 rounded-btn border border-bd bg-bg3 p-3">
          <p className="text-xs text-t2">Copy it now — it is not shown again.</p>
          <div className="mt-1 flex items-center gap-2">
            <code className="num flex-1 truncate text-sm">{made}</code>
            <Button onClick={() => navigator.clipboard?.writeText(made)}><Copy className="size-3.5" />Copy</Button>
            <Button variant="ghost" onClick={() => setMade(null)}>Done</Button>
          </div>
        </div>
      )}

      <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="What is it for?" className="max-w-xs" />
        <Button type="submit" loading={create.isPending} disabled={!name.trim()}><Plus className="size-4" />Create</Button>
      </form>
      <ErrorNote error={create.error} />

      {q.data?.data.length ? (
        <ul className="mt-3 divide-y divide-[var(--bd)]">
          {q.data.data.map((k) => (
            <li key={k.id} className="flex items-center gap-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{k.name} {k.revoked_at && <Badge tone="low">revoked</Badge>}</div>
                <div className="num text-xs text-t3">
                  {k.prefix}… · {k.scopes.join(', ')} · {k.last_used_at ? `used ${day(k.last_used_at)}` : 'never used'}
                </div>
              </div>
              {!k.revoked_at && (
                <button className="text-t3 hover:text-err" title="Revoke"
                  onClick={async () => { await del(`/v1/api-keys/${k.id}`); qc.invalidateQueries({ queryKey: ['keys'] }); }}>
                  <Trash2 className="size-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

type Hook = { id: string; url: string; events: string[]; active: boolean; failures: number; last_error: string | null; last_sent_at: string | null };

function Hooks() {
  const qc = useQueryClient();
  const [url, setUrl] = useState('');
  const [event, setEvent] = useState('audit.completed');
  const [secret, setSecret] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['hooks'], queryFn: () => get<{ data: Hook[]; events: string[] }>('/v1/webhooks') });
  const create = useMutation({
    mutationFn: () => post<Hook & { secret: string }>('/v1/webhooks', { url, events: [event] }),
    onSuccess: (r) => { setSecret(r.secret); setUrl(''); qc.invalidateQueries({ queryKey: ['hooks'] }); },
  });

  return (
    <Card>
      <h2 className="text-sm font-bold">Webhooks</h2>
      <p className="mt-0.5 max-w-prose text-sm text-t2">
        Every delivery is signed, so the receiver can tell our POST from anybody who learned the URL. Check the
        <code className="num"> x-adsaudit-signature</code> header: it is <code className="num">t=&lt;unix&gt;,v1=&lt;hmac-sha256 of "t.body"&gt;</code>,
        and the timestamp is inside the signature so a captured delivery cannot be replayed later.
      </p>

      {secret && (
        <div className="mt-3 rounded-btn border border-bd bg-bg3 p-3">
          <p className="text-xs text-t2">The signing secret. Copy it now — it is not shown again.</p>
          <div className="mt-1 flex items-center gap-2">
            <code className="num flex-1 truncate text-sm">{secret}</code>
            <Button onClick={() => navigator.clipboard?.writeText(secret)}><Copy className="size-3.5" />Copy</Button>
            <Button variant="ghost" onClick={() => setSecret(null)}>Done</Button>
          </div>
        </div>
      )}

      <form className="mt-3 flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="max-w-sm" />
        <Select value={event} onChange={(e) => setEvent(e.target.value)}>
          {(q.data?.events ?? ['audit.completed']).map((x) => <option key={x} value={x}>{x}</option>)}
        </Select>
        <Button type="submit" loading={create.isPending} disabled={!url.trim()}><Plus className="size-4" />Add</Button>
      </form>
      <ErrorNote error={create.error} />

      {q.data?.data.length ? (
        <ul className="mt-3 divide-y divide-[var(--bd)]">
          {q.data.data.map((h) => (
            <li key={h.id} className="flex items-center gap-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{h.url}</div>
                <div className="text-xs text-t3">
                  {h.events.join(', ')}
                  {!h.active && <> · <span className="text-err">switched off after repeated failures</span></>}
                  {h.last_sent_at && <> · last sent {day(h.last_sent_at)}</>}
                  {h.last_error && <> · {h.last_error}</>}
                </div>
              </div>
              <button className="text-t3 hover:text-err" title="Remove"
                onClick={async () => { await del(`/v1/webhooks/${h.id}`); qc.invalidateQueries({ queryKey: ['hooks'] }); }}>
                <Trash2 className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}
